import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Account, AccountType } from '@percent/domain/iam'
import { Badge, Icon, Modal, SearchInput } from '@percent/lemonade'
import { ACCOUNT_SEARCH_MIN_LENGTH, useAccountSearch } from './useAccountSearch'
import { useAccountSearchAccess } from './useAccountSearchAccess'
import {
  ACCOUNT_TYPE_PRECEDENCE,
  getAccountDetailRoute,
  getAccountTypeLabel,
  getPrimaryViewableAccountType
} from './accountSearch.utils'
import styles from './AccountSearch.module.scss'

// `primaryType` is null for an account with no known type: it's still shown (an
// all-account search must surface it) but carries no type badge and routes to the
// general account profile.
type ViewableResult = { account: Account; primaryType: AccountType | null }

const INPUT_TEST_ID = 'account-search-input'
const LISTBOX_ID = 'account-search-listbox'
const optionId = (accountId: string) => `account-search-option-${accountId}`

/**
 * Side-nav account search. The nav shows a compact trigger; activating it (click
 * or ⌘K / Ctrl+K) opens a modal panel holding the SearchInput and an inline
 * results list, over a dimmed backdrop. Composed from the lemonade Modal +
 * SearchInput + Badge. Hidden entirely when the user can't search any account
 * type.
 */
export function AccountSearch({ isCompact = false }: { isCompact?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { canSearch, canViewType, canViewTypeless } = useAccountSearchAccess()
  const { query, setQuery, results, isLoading } = useAccountSearch()
  const [opened, setOpened] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  // ⌘K / Ctrl+K opens the panel.
  useEffect(() => {
    if (!canSearch) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpened(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canSearch])

  // Reset the query when the panel closes. (The field itself is focused on open
  // by the Modal's focus trap via `data-autofocus` on the SearchInput.)
  useEffect(() => {
    if (!opened) {
      setQuery('')
    }
  }, [opened, setQuery])

  // A fresh query restarts the highlight at the top of the results.
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keep the keyboard-highlighted result scrolled into view.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  if (!canSearch) {
    return null
  }

  // Only surface accounts the user is allowed to open, so every result is
  // actionable rather than bouncing off an AccessRoute redirect.
  const viewableResults = results.reduce<ViewableResult[]>((acc, account) => {
    const primaryType = getPrimaryViewableAccountType(account, canViewType)

    if (primaryType) {
      acc.push({ account, primaryType })

      return acc
    }

    // No viewable typed match. An account with no known type at all still belongs
    // in an all-account search, so surface it as a general (badge-less) result
    // routed to the id-only account profile — gated on the permission that page
    // requires so it stays actionable. Accounts that *do* have a type the user
    // can't view stay filtered out (they'd bounce off an AccessRoute redirect).
    const hasKnownType = (account.type ?? []).some(type => ACCOUNT_TYPE_PRECEDENCE.includes(type))

    if (!hasKnownType && canViewTypeless) {
      acc.push({ account, primaryType: null })
    }

    return acc
  }, [])

  // `activeIndex` can outrun the current result set (e.g. after a new search
  // returns fewer rows), so clamp it before use.
  const activeResultIndex = viewableResults.length > 0 ? Math.min(activeIndex, viewableResults.length - 1) : -1
  const activeResult = viewableResults[activeResultIndex]

  const handleSelect = (result: ViewableResult) => {
    const route = getAccountDetailRoute(result.account, result.primaryType)

    if (route) {
      navigate(route)
      setOpened(false)
    }
  }

  // ↑/↓ move the highlight through the results (wrapping), Enter opens the
  // highlighted account — so the whole list is reachable from the search field.
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (viewableResults.length === 0) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex(index => (index + 1) % viewableResults.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(index => (index - 1 + viewableResults.length) % viewableResults.length)
    } else if (event.key === 'Enter' && activeResult) {
      event.preventDefault()
      handleSelect(activeResult)
    }
  }

  const renderResults = () => {
    if (query.trim().length < ACCOUNT_SEARCH_MIN_LENGTH) {
      return <p className={styles.message}>{t('navigation.accountSearch.emptyState')}</p>
    }

    if (isLoading) {
      return <p className={styles.message}>{t('navigation.accountSearch.loading')}</p>
    }

    if (viewableResults.length === 0) {
      return <p className={styles.message}>{t('navigation.accountSearch.noResults')}</p>
    }

    return (
      <ul ref={listRef} id={LISTBOX_ID} role="listbox" className={styles.list} data-testid="account-search-results">
        {viewableResults.map((result, index) => {
          const { account } = result
          const name = account.fullName?.trim()
          const isActive = index === activeResultIndex

          return (
            <li key={account.id} role="presentation">
              <button
                type="button"
                role="option"
                id={optionId(account.id)}
                aria-selected={isActive}
                data-active={isActive || undefined}
                className={styles.item}
                data-testid={`account-search-result-${account.id}`}
                onClick={() => handleSelect(result)}
                onMouseMove={() => setActiveIndex(index)}
              >
                <span className={styles.details}>
                  {name ? (
                    <>
                      <span className={styles.name}>{name}</span>
                      <span className={styles.email}>{account.email}</span>
                    </>
                  ) : (
                    <span className={styles.name}>{account.email}</span>
                  )}
                </span>
                {result.primaryType && <Badge variant="default">{getAccountTypeLabel(result.primaryType, t)}</Badge>}
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <>
      {/* The trigger is a presentational (read-only) SearchInput, or an icon-only
          field when compact, wrapped in a button-role element, so it looks like
          the modal's field but the whole area — including the search icon — opens
          the modal on click or Enter/Space. The wrapper owns the interaction; the
          inner field is inert (aria-hidden, not focusable, clicks pass through to
          here). */}
      <div
        className={styles.trigger}
        role="button"
        tabIndex={0}
        data-testid="account-search-trigger"
        aria-haspopup="dialog"
        aria-expanded={opened}
        aria-label={t('navigation.accountSearch.triggerLabel')}
        onClick={() => setOpened(true)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpened(true)
          }
        }}
      >
        {isCompact ? (
          <span
            aria-hidden
            className={styles.compactField}
            title={t('navigation.accountSearch.triggerLabel')}
            data-testid="account-search-compact-trigger"
          >
            <Icon name="search" size={6} color="gray500" />
          </span>
        ) : (
          <SearchInput
            aria-hidden
            tabIndex={-1}
            readOnly
            placeholder={t('navigation.accountSearch.triggerLabel')}
            value=""
            handleClearValue={() => undefined}
            inputSize="small"
          />
        )}
      </div>
      <Modal
        open={opened}
        onClose={() => setOpened(false)}
        closeOnClickOutside
        width={440}
        position="top-center-lowered"
        enableFocus={false}
      >
        <div className={styles.panel} data-testid="account-search-results-panel">
          <div className={styles.searchRow}>
            <SearchInput
              data-testid={INPUT_TEST_ID}
              className={styles.searchInput}
              data-autofocus
              role="combobox"
              aria-controls={LISTBOX_ID}
              aria-expanded={viewableResults.length > 0}
              aria-activedescendant={activeResult ? optionId(activeResult.account.id) : undefined}
              aria-autocomplete="list"
              placeholder={t('navigation.accountSearch.placeholder')}
              value={query}
              onChange={event => setQuery(event.target.value)}
              onKeyDown={handleInputKeyDown}
              handleClearValue={() => setQuery('')}
              inputSize="small"
            />
          </div>
          <div className={styles.results}>{renderResults()}</div>
        </div>
      </Modal>
    </>
  )
}
